// Data Access Layer: Resources
import { supabase } from '../supabase';
import type { Resource, TrackResourceWithDetails, Phase } from '../../types/database';

/**
 * Fetch all resources
 */
export async function getResources(): Promise<Resource[]> {
  const { data, error } = await supabase
    .from('resources')
    .select('*')
    .order('title');

  if (error) {
    console.error('Error fetching resources:', error);
    throw new Error(`Failed to fetch resources: ${error.message}`);
  }

  return data || [];
}

/**
 * Fetch a single resource by ID
 */
export async function getResourceById(resourceId: string): Promise<Resource | null> {
  const { data, error } = await supabase
    .from('resources')
    .select('*')
    .eq('id', resourceId)
    .single();

  if (error) {
    console.error('Error fetching resource:', error);
    throw new Error(`Failed to fetch resource: ${error.message}`);
  }

  return data;
}

/**
 * Fetch all resources for a specific track
 */
export async function getTrackResources(trackId: string): Promise<TrackResourceWithDetails[]> {
  const { data, error } = await supabase
    .from('track_resources')
    .select(`
      *,
      resource:resources(*)
    `)
    .eq('track_id', trackId)
    .order('order_index');

  if (error) {
    console.error('Error fetching track resources:', error);
    throw new Error(`Failed to fetch track resources: ${error.message}`);
  }

  return data || [];
}

/**
 * Fetch resources for a specific track and phase
 */
export async function getTrackResourcesByPhase(
  trackId: string,
  phase: Phase
): Promise<TrackResourceWithDetails[]> {
  const { data, error } = await supabase
    .from('track_resources')
    .select(`
      *,
      resource:resources(*)
    `)
    .eq('track_id', trackId)
    .eq('phase', phase)
    .order('order_index');

  if (error) {
    console.error('Error fetching track resources by phase:', error);
    throw new Error(`Failed to fetch track resources: ${error.message}`);
  }

  return data || [];
}
